import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  ClipboardList,
  FileText,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  User,
  Lock,
  Award,
  HelpCircle,
  ListChecks,
  UserCheck,
  Brain,
  Grid3x3,
  TrendingUp
} from 'lucide-react';
import useAuthStore from '../store/authStore';
import toast from 'react-hot-toast';

const SupervisorLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [examenesOpen, setExamenesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/supervisor/dashboard' },
    { icon: UserCheck, label: 'Movimiento', path: '/supervisor/asignar-postulantes' },
    { icon: FileText, label: 'Procesos', path: '/supervisor/procesos' },
    { icon: ClipboardList, label: 'Perfiles', path: '/supervisor/perfiles' },
    { 
      icon: Award, 
      label: 'Exámenes', 
      hasSubmenu: true,
      submenu: [
        { icon: Grid3x3, label: 'Raven', path: '/supervisor/examenes/raven' },
        { icon: Brain, label: 'DISC', path: '/supervisor/examenes/disc' },
        { icon: TrendingUp, label: 'IPV', path: '/supervisor/examenes/ipv' }
      ]
    },
    { icon: Users, label: 'Usuarios', path: '/supervisor/usuarios' },
  ];

  const handleLogout = () => {
    logout();
    toast.success('Sesión cerrada correctamente');
    navigate('/login');
  };

  const isActivePath = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 h-screen transition-transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } bg-white border-r border-neutral-200 w-64`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-neutral-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-secondary to-secondary-dark rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">P</span>
              </div>
              <div>
                <h1 className="font-bold text-lg text-neutral-900">PostPsic</h1>
                <p className="text-xs text-neutral-600">Supervisor</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <div className="space-y-1">
              {menuItems.map((item) => (
                <div key={item.path || item.label}>
                  {item.hasSubmenu ? (
                    <>
                      <button
                        onClick={() => setExamenesOpen(!examenesOpen)}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-neutral-700 hover:bg-neutral-100"
                      >
                        <item.icon size={20} />
                        <span className="font-medium">{item.label}</span>
                        {examenesOpen ? (
                          <ChevronDown size={16} className="ml-auto" />
                        ) : (
                          <ChevronRight size={16} className="ml-auto" />
                        )}
                      </button>
                      {examenesOpen && (
                        <div className="ml-4 mt-1 space-y-1">
                          {item.submenu.map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${
                                isActivePath(subItem.path)
                                  ? 'bg-secondary text-white shadow-md'
                                  : 'text-neutral-700 hover:bg-neutral-100'
                              }`}
                            >
                              <subItem.icon size={18} />
                              <span className="font-medium text-sm">{subItem.label}</span>
                              {isActivePath(subItem.path) && (
                                <ChevronRight size={14} className="ml-auto" />
                              )}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                        isActivePath(item.path)
                          ? 'bg-secondary text-white shadow-md'
                          : 'text-neutral-700 hover:bg-neutral-100'
                      }`}
                    >
                      <item.icon size={20} />
                      <span className="font-medium">{item.label}</span>
                      {isActivePath(item.path) && (
                        <ChevronRight size={16} className="ml-auto" />
                      )}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-neutral-200">
            
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut size={18} />
              <span className="font-medium">Cerrar Sesión</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`transition-all ${sidebarOpen ? 'ml-64' : 'ml-0'}`}>
        {/* Header */}
        <header className="bg-white border-b border-neutral-200 sticky top-0 z-30">
          <div className="px-6 py-4 flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            
            
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default SupervisorLayout;
